import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook to fetch CMS content for a page
 * Returns content with fallback support if CMS is empty
 * 
 * @param {string} pageName - The page name to fetch (home, programs, research, etc.)
 * @param {object} fallbackContent - Default content to use if CMS has no data
 * @returns {object} { content, loading, error, hasCustomContent, refetch }
 */
export function useCMSContent(pageName, fallbackContent = {}) {
  const [content, setContent] = useState(fallbackContent);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasCustomContent, setHasCustomContent] = useState(false);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/cms/public/content/${pageName}`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.has_custom_content && Object.keys(data.content).length > 0) {
          // Merge CMS content with fallback (CMS takes priority)
          setContent(prev => ({
            ...fallbackContent,
            ...data.content
          }));
          setHasCustomContent(true);
        } else {
          // Use fallback content
          setContent(fallbackContent);
          setHasCustomContent(false);
        }
      } else {
        // API error - use fallback
        setContent(fallbackContent);
        setHasCustomContent(false);
      }
    } catch (err) {
      console.error(`CMS fetch error for ${pageName}:`, err);
      setError(err);
      setContent(fallbackContent);
      setHasCustomContent(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [pageName]);

  return { content, loading, error, hasCustomContent, refetch: fetchContent };
}

/**
 * Helper to safely get nested content with fallback
 * @param {object} content - The content object
 * @param {string} path - Dot-notation path (e.g., 'hero.title')
 * @param {any} fallback - Fallback value if path doesn't exist
 */
export function getContent(content, path, fallback = '') {
  const keys = path.split('.');
  let result = content;
  
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = result[key];
    } else {
      return fallback;
    }
  }
  
  return result ?? fallback;
}

export default useCMSContent;
